import { Document, stringify } from 'yaml';
import { generatePopupJsonl } from '../../utils/hassPopupUtils.js';
import { HaspTemplatePage } from '../../Hasp/HaspPage.js';
import { buildPropertyExpression } from './propertyExpression.js';
import { buildBindingExpression } from './bindingGenerators.js';
import { renderConditionTemplate } from './conditionTemplate.js';
import { toHomeAssistantAction } from '../haActionYaml.js';
import { materializeTtsAction } from '../mediaPlayerTtsAction.js';
import { formatOpenHASPPluginObjectRef, resolveOpenHASPPluginBindings } from './pluginPropertyBindings.js';
import {
  btnmatrixFilterExpression,
  btnmatrixFilterValue,
  findBtnmatrixButton,
  getBtnmatrixButtons,
  isValidBtnmatrixFilter,
} from '../btnmatrixButtons.js';

// YAML indentation levels inside the event block — must match the
// openHASP HA component's expected format exactly.
const I1 = '        ';   // 8 spaces — list item inside an event handler
const I2 = '          '; // 10 spaces — keys inside the service call

/**
 * Emit a value as a YAML single-quoted scalar.
 *
 * Property expressions are always single-quoted (Jinja braces would
 * otherwise start a YAML flow mapping), so the one character that needs
 * escaping is the single quote itself — doubled, per the YAML spec. This
 * matters for any binding whose generated expression can carry
 * designer-authored literal text: a 'constant' value, or a 'mapping'
 * rule result on a string property (e.g. label.text → "don't"). Output
 * is byte-identical to the previous inline `'${expr}'` for every
 * quote-free expression, which is every expression generated before
 * string mappings existed.
 * @private
 */
function yamlSingleQuoted(value) {
  return `'${String(value).replace(/'/g, "''")}'`;
}

function toPlateAction(value) {
  const { action, ...serviceCall } = value;
  return { action, ...serviceCall };
}

/**
 * Resolve the entity used by the legacy embedded-popup generator. Standard
 * template export does not call this path; legacy popup state is explicit in
 * `_popupEntity` and is kept separate from ordinary widget bindings.
 */
function resolvePopupEntity(object, providerExtra) {
  return providerExtra?._popupEntity || null;
}

function stringifyPlateObject(value, { objectComment = '', eventComment = '' } = {}) {
  const document = new Document({ objects: [value] });
  const objectsPair = document.contents.items.find(pair => pair.key.value === 'objects');
  const objectNode = objectsPair.value.items[0];
  const objectPair = objectNode.items.find(pair => pair.key.value === 'obj');
  if (objectComment) objectPair.value.comment = ` ${objectComment}`;
  if (eventComment) {
    const eventPair = objectNode.items.find(pair => pair.key.value === 'event');
    if (eventPair) eventPair.key.commentBefore = ` ${eventComment}`;
  }

  const serialized = String(document);
  return serialized.slice(serialized.indexOf('\n') + 1);
}

/**
 * HomeAssistantGenerator
 *
 * Translates compiled AutomationModel[] (from AutomationCompiler) into
 * Home Assistant openHASP component YAML.
 *
 * Architecture rules:
 *  - Receives normalized models only — never raw AutomationModel instances.
 *  - Does not modify the AST.
 *  - hass_config.properties are passed directly (state subscriptions; not AST).
 *  - Entity IDs live in action.parameters under key 'entity_id'.
 *  - Conditions are generic CompareConditions — no HA-specific types.
 *  - Output is identical to the legacy hassYamlGenerator.js for the same input.
 */
export class HomeAssistantGenerator {
  /**
   * Generate HA component YAML for all automations belonging to one widget.
   *
   * @param {object}           options
   * @param {number}           options.page_id
   * @param {number}           options.hasp_id
   * @param {object}           options.object      — HaspObject (.obj, .comment, .text)
   * @param {object}           options.hass_config  — raw hass_config (properties used directly)
   * @param {AutomationModel[]} options.models      — compiled models for this widget's events
   * @param {boolean}          [options.useTag]     — retained for compatibility; does not invent action targets
   * @returns {string}
   */
  generate({ page_id, hasp_id, object, hass_config, models, useTag = false, validationErrors }) {
    const objType = object?.obj ?? '';

    const plateObject = this._buildPlateObject({
      page_id, hasp_id, object, hass_config, models, useTag, validationErrors,
    });
    if (!plateObject) return '';
    return stringifyPlateObject(plateObject.value, {
      objectComment: object?.comment || object?.text || '',
      eventComment: plateObject.eventComment,
    });
  }

  _buildPlateObject({ page_id, hasp_id, object, hass_config, models, useTag, validationErrors }) {
    const objType = object?.obj ?? '';
    const rawBindings = models.flatMap(m => m.bindings ?? []);
    const usesCompiledBindings = rawBindings.length > 0;
    const bindingInput = usesCompiledBindings
      ? rawBindings
      : (hass_config?.properties ?? []).filter(Boolean).map(binding => ({
          strategy: 'state',
          ...binding,
        }));
    const { bindings, errors } = resolveOpenHASPPluginBindings(object, bindingInput);
    validationErrors?.push(...errors);
    this._validateBtnmatrixFilters({ page_id, hasp_id, object, models, validationErrors });
    const activeModels = models.filter(m => m.triggers.length > 0 && m.actions.length > 0);
    if (bindings.length === 0 && activeModels.length === 0) return null;

    const objectRef = formatOpenHASPPluginObjectRef(page_id, object, hasp_id);
    const value = { obj: objectRef };
    const visibleBindings = bindings.filter(binding => binding.property);
    if (visibleBindings.length > 0) {
      value.properties = usesCompiledBindings
        ? Object.fromEntries(visibleBindings.map(binding => [binding.property, buildBindingExpression(binding)]))
        : Object.fromEntries(visibleBindings.map(binding => [
          binding.property,
          this._buildPropertyExpression(binding, useTag, objType),
        ]));
    }

    const event = objType === 'msgbox'
      ? this._buildMsgboxEvents(activeModels)
      : this._buildEvents(activeModels, useTag, object);
    if (Object.keys(event).length > 0) value.event = event;

    return {
      value,
      eventComment: objType === 'msgbox' && Object.keys(event).length > 0
        ? 'val contains the 0-based index of the button that was pressed.'
        : '',
    };
  }

  _buildEvents(models, useTag, object) {
    const byEvent = new Map();
    for (const model of models) {
      const eventName = model.triggers[0]?.event ?? '';
      if (!byEvent.has(eventName)) byEvent.set(eventName, []);
      byEvent.get(eventName).push(model);
    }

    return Object.fromEntries([...byEvent].map(([eventName, eventModels]) => {
      // Keep the established ordering for every widget except btnmatrix.
      // Button filters need the specialized placement below so an unrelated
      // Any-button action is not moved across the combined choose block.
      if (object?.obj !== 'btnmatrix') {
        const actions = [];
        const conditioned = [];
        for (const model of eventModels) {
          if (this._eventConditions(model, object).length > 0 && model.actions.length > 0) {
            conditioned.push(model);
          } else {
            actions.push(...model.actions.map(action => this._buildActionObject(
              action, model._providerExtra, useTag, object,
            )));
          }
        }
        if (conditioned.length > 0) actions.push(this._buildChooseObject(conditioned, object));
        return [eventName, actions];
      }

      const actions = [];
      const conditioned = [];
      let choosePosition = -1;
      for (const model of eventModels) {
        if (this._eventConditions(model, object).length > 0 && model.actions.length > 0) {
          conditioned.push(model);
          if (choosePosition < 0) {
            choosePosition = actions.length;
            actions.push(null);
          }
        } else {
          actions.push(...model.actions.map(action => this._buildActionObject(
            action, model._providerExtra, useTag, object,
          )));
        }
      }
      if (choosePosition >= 0) actions[choosePosition] = this._buildChooseObject(conditioned, object);
      return [eventName, actions.filter(Boolean)];
    }));
  }

  _buildMsgboxEvents(models) {
    if (models.length === 0) return {};
    return {
      down: [{
        choose: models.map((model, index) => ({
          conditions: (model.conditions.length > 0
            ? model.conditions.map(condition => ({
              condition: 'template',
              value_template: this._renderCondition(condition),
            }))
            : [{ condition: 'template', value_template: `{{ val == ${index} }}` }]),
          sequence: model.actions.map(action => this._buildActionObject(
            action, model._providerExtra, false, null,
          )),
        })),
      }],
    };
  }

  _buildChooseObject(models, object) {
    return {
      choose: models.map(model => ({
        conditions: this._eventConditions(model, object).map(condition => ({
          condition: 'template',
          value_template: condition.value_template ?? this._renderCondition(condition),
        })),
        sequence: model.actions.map(action => this._buildActionObject(
          action, model._providerExtra, false, object,
        )),
      })),
    };
  }

  _eventConditions(model, object) {
    const conditions = [];
    if (object?.obj === 'btnmatrix') {
      const trigger = model.triggers[0];
      const filterValue = btnmatrixFilterValue(trigger);
      if (filterValue !== null) {
        conditions.push({
          value_template: btnmatrixFilterExpression(trigger) ?? '{{ false }}',
        });
      }
    }
    conditions.push(...model.conditions);
    return conditions;
  }

  _validateBtnmatrixFilters({ page_id, hasp_id, object, models, validationErrors }) {
    if (object?.obj !== 'btnmatrix' || !validationErrors) return;
    const buttons = getBtnmatrixButtons(object);
    for (const model of models) {
      const trigger = model.triggers[0];
      const value = btnmatrixFilterValue(trigger);
      if (value === null) continue;
      if (!isValidBtnmatrixFilter(trigger) || !findBtnmatrixButton(buttons, value)) {
        validationErrors.push(
          `Object p${page_id}b${hasp_id}: btnmatrix event ${trigger?.event || '—'} references missing button index ${value}.`,
        );
      }
    }
  }

  _buildActionObject(action, providerExtra, useTag, object) {
    action = materializeTtsAction(action);
    if (action.type === 'delay') {
      const seconds = action.parameters.find(parameter => parameter.key === 'seconds')
        ?.value?.toString() || '1';
      return { delay: { seconds } };
    }

    if (action.provider === 'openhasp' && action.target == null && action.data == null) {
      return this._buildOpenhaspObject(action, providerExtra, object);
    }

    const value = toHomeAssistantAction(action, { useTag, object });
    if (action.target == null && action.provider === 'esphome') delete value.target;
    if (action.data == null && action.provider === 'homeassistant') delete value.data;
    return toPlateAction(value);
  }

  _buildOpenhaspObject(action, providerExtra, object) {
    const extra = providerExtra ?? {};
    const openhaspAction = extra.action ?? action.service;
    const entityParam = action.parameters.find(parameter => parameter.key === 'entity_id');
    const plate = entityParam ? entityParam.value.toString() : 'openhasp.MY_PLATE';
    let keyword = '';
    if (openhaspAction === 'command') keyword = 'jsonl';
    if (openhaspAction === 'delete' && extra._deleteTargetPageId != null && extra._deleteObjectId != null) {
      keyword = `p${extra._deleteTargetPageId}b${extra._deleteObjectId}.delete`;
    }

    const data = { keyword };
    const popupEntity = resolvePopupEntity(object, extra);
    if (openhaspAction === 'command' && extra._popupTemplateId && popupEntity) {
      const layer = object?.getLayer?.();
      const templatePage = layer?.getChildren?.()?.find?.(
        child => child instanceof HaspTemplatePage && child.haspid === extra._popupTemplateId,
      );
      if (templatePage) {
        const jsonl = generatePopupJsonl(
          templatePage,
          popupEntity,
          extra._popupTargetPageId,
          extra._popupRootObjectId,
        ).trim();
        if (jsonl) data.parameters = jsonl;
      }
    }

    return {
      action: 'openhasp.command',
      target: { entity_id: plate },
      data,
    };
  }

  /*
   * The methods below are retained for the legacy generator internals and
   * special consumers. Plate output above is built as a data object and
   * serialized once by stringifyPlateObject().
   */

  // ── Properties ──────────────────────────────────────────────────────────────

  /** @private */
  _appendProperties(lines, hass_config, useTag, objType) {
    const props        = (hass_config?.properties ?? []).filter(Boolean);
    const visibleProps = props.filter(p => p.property);
    if (visibleProps.length === 0) return;

    lines.push(`    properties:`);
    for (const prop of visibleProps) {
      const expr = this._buildPropertyExpression(prop, useTag, objType);
      lines.push(`      "${prop.property}": ${yamlSingleQuoted(expr)}`);
    }
  }

  /**
   * Build the Jinja2 expression for a single property binding.
   * Delegates to the shared builder (also used by migration).
   * @private
   */
  _buildPropertyExpression(prop, _useTag, objType) {
    return buildPropertyExpression(prop, objType);
  }

  /**
   * Emit the properties section from AutomationBinding instances
   * (the State Binding blocks of the unified Automation Editor).
   * Dispatches purely on binding.strategy — see bindingGenerators.js.
   * @private
   */
  _appendBindingProperties(lines, bindings) {
    const visible = bindings.filter(b => b.property);
    if (visible.length === 0) return;

    lines.push(`    properties:`);
    for (const binding of visible) {
      const expr = buildBindingExpression(binding);
      lines.push(`      "${binding.property}": ${yamlSingleQuoted(expr)}`);
    }
  }

  // ── Standard events (non-msgbox) ───────────────────────────────────────────

  /** @private */
  _appendStandardEvents(lines, models, useTag, object) {
    // Group models by their first trigger's event name
    const byEvent = new Map();
    for (const model of models) {
      const eventName = model.triggers[0]?.event ?? '';
      if (!byEvent.has(eventName)) byEvent.set(eventName, []);
      byEvent.get(eventName).push(model);
    }

    lines.push(`    event:`);
    for (const [eventName, eventModels] of byEvent) {
      lines.push(`      "${eventName}":`);

      // Unconditioned models emit plain service calls; conditioned models
      // (if/else branches in the designer) are regrouped into one choose block.
      const conditioned = [];
      for (const model of eventModels) {
        if (model.conditions.length > 0 && model.actions.length > 0) {
          conditioned.push(model);
          continue;
        }
        for (const action of model.actions) {
          lines.push(...this._buildActionLines(action, model._providerExtra, useTag, object));
        }
      }

      if (conditioned.length > 0) {
        lines.push(...this._buildChooseLines(conditioned, useTag, object));
      }
    }
  }

  /**
   * Render conditioned models (designer if/else branches) as a single HA
   * choose block. Each model becomes one branch: its conditions (AND) and
   * its actions as the sequence.
   *
   * Branches always use literal entity IDs, never {{ tag }} — matching the
   * msgbox choose block below. {{ tag }} means "the one entity this widget
   * is bound to"; a choose block exists specifically to let branches act on
   * different entities/services, so collapsing them onto a shared tag is
   * never correct even when every branch happens to reference one entity.
   * @private
   */
  _buildChooseLines(models, _useTag, object) {
    const lines = [`${I1}- choose:`];

    for (const model of models) {
      lines.push(`${I1}    - conditions:`);
      for (const cond of model.conditions) {
        lines.push(
          `${I1}        - condition: template`,
          `${I1}          value_template: "${this._renderCondition(cond)}"`,
        );
      }
      lines.push(`${I1}      sequence:`);
      for (const action of model.actions) {
        lines.push(...this._buildActionLines(action, model._providerExtra, false, object, `${I1}        `));
      }
    }

    return lines;
  }

  // ── msgbox (choose block) ──────────────────────────────────────────────────

  /** @private */
  _generateMsgboxYaml({ page_id, hasp_id, object, models }) {
    const comment      = object?.comment || '';
    const activeModels = models.filter(m => m.triggers.length > 0 && m.actions.length > 0);

    // Matches the legacy behavior: return '' when no events are configured
    if (activeModels.length === 0) return '';

    const lines = [
      `  - obj: "p${page_id}b${hasp_id}"${comment ? ` # ${comment}` : ''}`,
      `    # val contains the 0-based index of the button that was pressed.`,
      `    event:`,
      `      "down":`,
      `        - choose:`,
    ];

    for (const model of activeModels) {
      // Render CompareConditions as Jinja2 template expressions.
      // Legacy models without conditions default to the first button.
      const condExprs = model.conditions.length > 0
        ? model.conditions.map(c => this._renderCondition(c))
        : ['{{ val == 0 }}'];

      lines.push(`          - conditions:`);
      for (const expr of condExprs) {
        lines.push(
          `              - condition: template`,
          `                value_template: "${expr}"`,
        );
      }

      lines.push(`            sequence:`);
      for (const action of model.actions) {
        lines.push(...this._buildActionLines(action, model._providerExtra, false, object, `              `));
      }
    }

    return lines.join('\n') + '\n';
  }

  // ── Condition rendering ────────────────────────────────────────────────────

  /**
   * Translate a generic CompareCondition into a Jinja2 template expression.
   * left=ref('val'), op='==', right=literal('0') → "{{ val == 0 }}"
   * @private
   */
  _renderCondition(cond) {
    return renderConditionTemplate(cond);
  }

  // ── Action line builders ───────────────────────────────────────────────────

  /**
   * @private
   * @param {string} [indent] — indentation of the `- action:` list item;
   *                            defaults to a plain event handler entry.
   */
  _buildActionLines(action, providerExtra, useTag, object, indent = I1) {
    action = materializeTtsAction(action);
    const { provider } = action;
    const keyIndent = `${indent}  `;

    // Non-service delay action ("Delay n seconds" block)
    if (action.type === 'delay') {
      const secondsParam = action.parameters.find(p => p.key === 'seconds');
      const seconds      = secondsParam?.value?.toString() || '1';
      return [
        `${indent}- delay:`,
        `${keyIndent}  seconds: ${seconds}`,
      ];
    }

    if (provider === 'openhasp' && action.target == null && action.data == null) {
      return this._buildOpenhaspLines(action, providerExtra, object, indent);
    }

    const value = toHomeAssistantAction(action, { useTag, object });
    // Preserve the established routing for legacy parameter-based actions.
    // Native structured target/data values are always emitted as entered.
    if (action.target == null && provider === 'esphome') delete value.target;
    if (action.data == null && provider === 'homeassistant') delete value.data;
    return this._buildYamlListItem(toPlateAction(value), indent);
  }

  /** @private */
  _buildOpenhaspLines(action, providerExtra, object, indent = I1) {
    const ex             = providerExtra ?? {};
    const openhaspAction = ex.action ?? action.service;
    const keyIndent      = `${indent}  `;

    // Plate entity comes from the entity_id parameter
    const entityParam = action.parameters.find(p => p.key === 'entity_id');
    const plate       = entityParam ? entityParam.value.toString() : 'openhasp.MY_PLATE';

    let keyword = '';
    if (openhaspAction === 'command') {
      keyword = 'jsonl';
    } else if (openhaspAction === 'delete') {
      if (ex._deleteTargetPageId != null && ex._deleteObjectId != null) {
        keyword = `p${ex._deleteTargetPageId}b${ex._deleteObjectId}.delete`;
      }
    }

    const lines = [
      `${indent}- action: openhasp.command`,
      `${keyIndent}target:`,
      `${keyIndent}  entity_id: "${plate}"`,
      `${keyIndent}data:`,
      `${keyIndent}  keyword: "${keyword}"`,
    ];

    // Popup command: generate JSONL from the designer template page at export time
    const popupEntity = resolvePopupEntity(object, ex);
    if (openhaspAction === 'command' && ex._popupTemplateId && popupEntity) {
      const layer        = object?.getLayer?.();
      const templatePage = layer?.getChildren?.()?.find?.(
        c => c instanceof HaspTemplatePage && c.haspid === ex._popupTemplateId
      );
      if (templatePage) {
        const jsonlStr  = generatePopupJsonl(
          templatePage,
          popupEntity,
          ex._popupTargetPageId,
          ex._popupRootObjectId,
        );
        if (jsonlStr.trim()) {
          const jsonlLines = jsonlStr.trim().split('\n').filter(l => l.trim());
          lines.push(`${keyIndent}  parameters: |-`);
          for (const l of jsonlLines) {
            lines.push(`${keyIndent}    ${l.trim()}`);
          }
        }
      }
    }

    return lines;
  }

  /**
   * Build target: entity_id: … lines from an action's parameters.
   * Handles single entity (literal), multiple entities (list), and {{ tag }}.
   * @private
   */
  _buildEntityTargetFromParams(parameters, _useTag, indent) {
    const entityParam = parameters.find(p => p.key === 'entity_id');
    if (!entityParam) return [];

    const val = entityParam.value;
    if (val.kind === 'list') {
      const items = val.items.filter(Boolean);
      if (items.length === 0) return [];
      if (items.length === 1) {
        return [`${indent}target:`, `${indent}  entity_id: "${items[0]}"`];
      }
      return [
        `${indent}target:`,
        `${indent}  entity_id:`,
        ...items.map(e => `${indent}    - "${e}"`),
      ];
    }

    const entityStr = val.toString();
    if (!entityStr) return [];
    return [`${indent}target:`, `${indent}  entity_id: "${entityStr}"`];
  }

  /**
   * Build data: … lines from an action's parameters, excluding entity_id.
   * @private
   */
  _buildDataLines(parameters, indent) {
    const dataParams = parameters.filter(p => p.key !== 'entity_id');
    if (dataParams.length === 0) return [];
    return [
      `${indent}data:`,
      ...dataParams.map(p => `${indent}  ${p.key}: "${p.value.toString()}"`),
    ];
  }

  /** Render a recursively structured value as one YAML list item. */
  _buildYamlListItem(value, indent) {
    return stringify([value], { lineWidth: 0 })
      .trimEnd()
      .split('\n')
      .map(line => `${indent}${line}`);
  }
}
